import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'motion/react';
import { computeTokenCost } from '@agentbrush/shared';
import { useSession } from './hooks/use-session.js';
import { useSubmitMask } from './hooks/use-submit-mask.js';
import { useAcceptResult } from './hooks/use-accept-result.js';
import { useRevertEdit } from './hooks/use-revert-edit.js';
import { useFinishSession } from './hooks/use-finish-session.js';
import { EditorCanvas, type EditorCanvasHandle } from './components/editor-canvas.js';
import { Toolbar } from './components/toolbar.js';
import { DescriptionInput } from './components/description-input.js';
import { EditorHeader } from './components/editor-header.js';
import { ZoomControl } from './components/zoom-control.js';
import { KeyboardHint } from './components/keyboard-hint.js';
import { SkyrimLoading } from './components/skyrim-loading.js';
import { ReviewPill } from './components/review-pill.js';
import { CelebrationMoment } from './components/celebration-moment.js';
import { SplashScreen } from './components/splash-screen/splash-screen.js';
import { useEditorStore } from '../../stores/editor.js';
import { useQuotaQuery } from '../../hooks/use-quota-query.js';

const CELEBRATION_DURATION_MS = 700;

export function EditorPage() {
  const { t } = useTranslation('editor');
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const expires = searchParams.get('expires');
  const nonce = searchParams.get('nonce');
  const [imageError, setImageError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imageMeta, setImageMeta] = useState<{ width: number; height: number } | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  // Story 7.7: one-shot splash screen. `showSplash` lives in component state
  // (not Zustand) so it resets naturally on full page reload but NOT on
  // iteration via `keepEditing` (which only touches the store).
  const [showSplash, setShowSplash] = useState(true);
  const [imagePreloaded, setImagePreloaded] = useState(false);
  const canvasHandleRef = useRef<EditorCanvasHandle | null>(null);
  const prevSubmissionStatusRef = useRef<string>('idle');

  const handleImageError = useCallback(() => {
    setImageError(t('error.imageLoadFailed'));
  }, [t]);

  const { data, isLoading, error } = useSession(sessionId, token, expires, nonce);
  const { submit } = useSubmitMask(sessionId);
  const { accept, isAccepting } = useAcceptResult(sessionId);
  const { revert, isReverting } = useRevertEdit(sessionId);
  const { finish, isFinishing } = useFinishSession(sessionId);
  const { data: quotaData } = useQuotaQuery();
  const submissionStatus = useEditorStore((s) => s.submissionStatus);
  const undoStackLength = useEditorStore((s) => s.undoStack.length);
  const maskData = useEditorStore((s) => s.maskData);
  const description = useEditorStore((s) => s.description);
  const setDescription = useEditorStore((s) => s.setDescription);
  const currentImageUrl = useEditorStore((s) => s.currentImageUrl);
  const setCurrentImageUrl = useEditorStore((s) => s.setCurrentImageUrl);
  const canRevert = useEditorStore((s) => s.canRevert);
  const iterationsAccepted = useEditorStore((s) => s.iterationsAccepted);
  const sessionFinished = useEditorStore((s) => s.sessionFinished);
  // Finish (and the tab-close beacon) is armed as soon as a submit has
  // produced a deliverable on the server. That's true if the user has
  // accepted anything (iterationsAccepted > 0) OR if a fresh result is
  // sitting on the server in awaiting_acceptance — canRevert is the
  // local proxy for "did the most recent submit succeed and is still
  // recoverable on the server" (cleared only by accept/revert).
  const canFinish = iterationsAccepted > 0 || canRevert;
  const quality = useEditorStore((s) => s.quality);

  // hasMask: true if undoStack has entries or maskData is non-null
  const hasMask = undoStackLength > 0 || maskData !== null;

  const editCost = computeTokenCost({ quality, size: '1024x1024' });

  // Initialize store description from session instruction (only if empty)
  useEffect(() => {
    if (data?.instruction && useEditorStore.getState().description === '') {
      setDescription(data.instruction);
    }
  }, [data, setDescription]);

  // Initialize currentImageUrl from the session source image on first load.
  // Use getState() as a guard so iterations (keepEditing → currentImageUrl
  // swapped to resultImageUrl) don't get reverted when `data` re-references.
  useEffect(() => {
    if (data?.imageUrl && useEditorStore.getState().currentImageUrl === null) {
      setCurrentImageUrl(data.imageUrl);
    }
  }, [data, setCurrentImageUrl]);

  // Story 7.7 AC6: preload the source image in the background while the
  // splash is up. The splash holds past its 1500ms baseline until the image
  // has decoded, so the canvas underneath is ready the instant the splash
  // lifts — no FOUC, no layout shift. On decode/load failure we still flip
  // the flag so the splash doesn't hang forever; the existing
  // `handleImageError` path in <EditorCanvas> surfaces the error afterwards.
  useEffect(() => {
    if (!data?.imageUrl) return;
    let cancelled = false;
    const img = new Image();
    img.src = data.imageUrl;
    const markLoaded = () => {
      if (!cancelled) setImagePreloaded(true);
    };
    if (typeof img.decode === 'function') {
      img.decode().then(markLoaded).catch(markLoaded);
    } else {
      img.onload = markLoaded;
      img.onerror = markLoaded;
    }
    return () => {
      cancelled = true;
    };
  }, [data?.imageUrl]);

  // One-shot celebration trigger: fires when submissionStatus transitions
  // from 'submitting' to 'success'. Hides the celebration after 700ms, or
  // immediately if the user leaves 'success' before the timer fires (e.g.
  // clicking Keep editing or Accept within the 700ms window).
  useEffect(() => {
    const prev = prevSubmissionStatusRef.current;
    prevSubmissionStatusRef.current = submissionStatus;

    if (prev === 'submitting' && submissionStatus === 'success') {
      setShowCelebration(true);
      const timerId = window.setTimeout(() => {
        setShowCelebration(false);
      }, CELEBRATION_DURATION_MS);
      return () => {
        window.clearTimeout(timerId);
      };
    }

    if (submissionStatus !== 'success') {
      // Ensure the celebration does not linger after the status moves away
      // from 'success' (accept, keep editing, or a subsequent error).
      setShowCelebration(false);
    }
    return undefined;
  }, [submissionStatus]);

  const handleSubmit = useCallback(() => {
    const maskDataUrl = canvasHandleRef.current?.exportMask();
    if (!maskDataUrl) return;
    submit(maskDataUrl, description);
  }, [submit, description]);

  // Implicit-finish on tab close. Only fires if the user has accepted at
  // least one iteration AND hasn't already finished — otherwise the server
  // refuses (no result to deliver) or the call is redundant.
  //
  // Uses fetch with `keepalive: true` so the request survives unload. We
  // can't use navigator.sendBeacon here because the /finish endpoint
  // requires an Idempotency-Key header that sendBeacon can't set.
  useEffect(() => {
    if (!sessionId) return;
    const onUnload = () => {
      const state = useEditorStore.getState();
      // Same gate as the Finish button: deliver as long as there's a result
      // on the server. canRevert is the local proxy for that.
      if (state.sessionFinished) return;
      if (state.iterationsAccepted === 0 && !state.canRevert) return;
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const idempotencyKey = crypto.randomUUID();
      try {
        void fetch(`${apiBase}/v1/edit/${sessionId}/finish`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
          },
          body: '{}',
          keepalive: true,
        });
      } catch {
        // Best-effort — nothing useful to do if the unload fetch fails.
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [sessionId]);

  // Story 7.7 AC6: max(splash timer, session ready, image preloaded). If the
  // session errored, still flip the gate true so the splash unmounts at its
  // baseline duration and the error screen can render — don't trap the user
  // behind a permanent brand moment.
  const canSplashComplete = !isLoading && (imagePreloaded || Boolean(error || imageError));

  // Compute the page body using the existing loading / error / main branches.
  // The body is ALWAYS rendered — the splash sits as a sibling on top of it
  // while `showSplash` is true. This is what AC10 requires: the editor tree
  // must mount in parallel with the splash so the canvas, React effects, and
  // image preload are all running during the splash window.
  let body: ReactNode;
  if (isLoading) {
    body = (
      <div
        role="status"
        aria-busy="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-body)',
        }}
      >
        {t('loading')}
      </div>
    );
  } else if (error || imageError || !data) {
    body = (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          color: 'var(--color-text-primary)',
          fontFamily: 'var(--font-sans)',
          gap: 'var(--space-3)',
          padding: 'var(--space-4)',
        }}
      >
        <h1
          style={{
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            margin: 0,
          }}
        >
          {t('error.sessionTitle')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-text-secondary)',
            margin: 0,
            textAlign: 'center',
            maxWidth: '400px',
          }}
        >
          {error ?? imageError ?? t('error.unexpected')}
        </p>
      </div>
    );
  } else {
    // Prefer the store's currentImageUrl once it's initialized; fall back to
    // data.imageUrl for the very first render (before the init effect runs).
    const canvasImageUrl = currentImageUrl ?? data.imageUrl;
    body = (
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
      >
        <EditorHeader
          imageUrl={canvasImageUrl}
          imageMeta={imageMeta}
          saved={iterationsAccepted > 0 || sessionFinished}
          canFinish={canFinish}
          sessionFinished={sessionFinished}
          isFinishing={isFinishing}
          onFinish={() => void finish()}
        />

        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            // Diagonal checker — bumped contrast over the mockup defaults so
            // the grid actually reads as texture instead of disappearing
            // against the dark canvas.
            backgroundColor: '#0d0d0d',
            backgroundImage:
              'linear-gradient(45deg, #1d1d1d 25%, transparent 25%), ' +
              'linear-gradient(-45deg, #1d1d1d 25%, transparent 25%), ' +
              'linear-gradient(45deg, transparent 75%, #1d1d1d 75%), ' +
              'linear-gradient(-45deg, transparent 75%, #1d1d1d 75%)',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
          }}
          data-testid="editor-stage"
        >
          <EditorCanvas
            imageUrl={canvasImageUrl}
            onImageError={handleImageError}
            onZoomChange={setZoomLevel}
            onImageMeta={setImageMeta}
            canvasHandleRef={canvasHandleRef}
          />
          <Toolbar disabled={sessionFinished} />
          <ZoomControl
            zoomLevel={zoomLevel}
            canvasHandleRef={canvasHandleRef}
            disabled={sessionFinished}
          />
          <KeyboardHint remainingTokens={quotaData?.tokenBalance ?? null} />
          <AnimatePresence>
            {submissionStatus === 'submitting' && <SkyrimLoading />}
          </AnimatePresence>
          <AnimatePresence>{showCelebration && <CelebrationMoment />}</AnimatePresence>

          {/* Floating Accept + Revert pill above the dock — visible only
              while a fresh result is in review. Typing in the dock or
              painting on the canvas dismisses it (see dismissReview). */}
          <AnimatePresence>
            {submissionStatus === 'success' && (
              <ReviewPill
                onAccept={() => void accept()}
                onRevert={() => void revert()}
                canRevert={canRevert}
                isAccepting={isAccepting}
                isReverting={isReverting}
              />
            )}
          </AnimatePresence>

          <div
            style={{
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: 16,
              zIndex: 25,
            }}
          >
            <DescriptionInput
              hasMask={hasMask}
              onSubmit={handleSubmit}
              cost={editCost}
              remainingTokens={quotaData?.tokenBalance ?? null}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showSplash && (
        <SplashScreen canComplete={canSplashComplete} onComplete={() => setShowSplash(false)} />
      )}
      {body}
    </>
  );
}
